import { resetAppNavSession } from '@/lib/app-nav-state';
import { ACTIVITY_HEADER, IDLE_TIMEOUT_HEADER } from '@/lib/session/constants';
import { activityBus } from './activity-bus';
import type { RequestOptions } from './types';

async function parseJson<T>(res: Response): Promise<T> {
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export async function tpFetch(
    input: RequestInfo | URL,
    options: RequestOptions = {},
): Promise<Response> {
    const { initiator = 'user', ...init } = options;
    const headers = new Headers(init.headers);

    if (initiator === 'user') {
        headers.set(ACTIVITY_HEADER, 'true');
    }

    const response = await fetch(input, { ...init, headers, credentials: 'include' });

    if (response.status === 401 && response.headers.get(IDLE_TIMEOUT_HEADER) === 'true') {
        if (typeof window !== 'undefined') {
            // Idle timeout is an automatic, this-browser logout — use a local sign-out
            // so we don't GlobalSignOut the user off all their other devices/tabs.
            // Also clear bookmark/history nav so a later login cannot restore this state.
            resetAppNavSession(window.location.pathname || '/');
            window.location.replace('/api/sign-out?scope=local');
        }
        return response;
    }

    if (initiator === 'user' && response.ok) {
        activityBus.emit();
    }

    return response;
}

export const api = {
    get: <T = unknown>(url: string, opts?: RequestOptions) =>
        tpFetch(url, { ...opts, method: 'GET' }).then(parseJson<T>),
    post: <T = unknown>(url: string, body?: unknown, opts?: RequestOptions) =>
        tpFetch(url, {
            ...opts,
            method: 'POST',
            headers: { 'content-type': 'application/json', ...(opts?.headers ?? {}) },
            body: body ? JSON.stringify(body) : undefined,
        }).then(parseJson<T>),
};
